import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TAX_CREDITS, SETTLEMENTS, formatMoney, formatDeadline } from "@/lib/mockData";
import { useApp } from "@/context/AppContext";
import { ArrowUpRight, Wallet, Receipt, Landmark, CheckCircle2, Clock, ExternalLink, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Claims() {
    const { totals, unclaimed, profile, refreshUnclaimed } = useApp();
    const [tab, setTab] = useState("all");
    const [state, setState] = useState(profile.state || "NY");

    const onStateChange = async (s) => {
        setState(s);
        await refreshUnclaimed(s);
    };

    return (
        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-7xl mx-auto">
            <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Money owed</p>
                <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight">Recover what's already yours</h1>
                <p className="mt-3 text-muted-foreground max-w-2xl">Unclaimed property across all 50 states + DC, missed tax credits, and active class-action settlements.</p>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
                <SummaryBar label="Unclaimed property" total={totals.unclaimed} count={unclaimed.length} icon={Wallet} tone="primary" />
                <SummaryBar label="Missed tax credits" total={totals.credits} count={TAX_CREDITS.length} icon={Receipt} tone="accent" />
                <SummaryBar label="Open settlements" total={totals.settlements} count={SETTLEMENTS.filter(s => s.status === "Open").length} icon={Landmark} tone="terracotta" />
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mt-10">
                <TabsList className="bg-secondary/50 rounded-full h-11 p-1">
                    <TabsTrigger value="all" className="rounded-full px-4 data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="claims-tab-all">All ({unclaimed.length + TAX_CREDITS.length + SETTLEMENTS.length})</TabsTrigger>
                    <TabsTrigger value="unclaimed" className="rounded-full px-4 data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="claims-tab-unclaimed">Property ({unclaimed.length})</TabsTrigger>
                    <TabsTrigger value="credits" className="rounded-full px-4 data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="claims-tab-credits">Credits ({TAX_CREDITS.length})</TabsTrigger>
                    <TabsTrigger value="settlements" className="rounded-full px-4 data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="claims-tab-settlements">Settlements ({SETTLEMENTS.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="all" className="mt-6 space-y-8">
                    <UnclaimedList items={unclaimed} state={state} onStateChange={onStateChange} />
                    <CreditsList />
                    <SettlementsList />
                </TabsContent>
                <TabsContent value="unclaimed" className="mt-6"><UnclaimedList items={unclaimed} state={state} onStateChange={onStateChange} /></TabsContent>
                <TabsContent value="credits" className="mt-6"><CreditsList /></TabsContent>
                <TabsContent value="settlements" className="mt-6"><SettlementsList /></TabsContent>
            </Tabs>
        </div>
    );
}

function SummaryBar({ label, total, count, icon: Icon, tone }) {
    const toneMap = { primary: "bg-primary text-primary-foreground", accent: "bg-accent text-accent-foreground", terracotta: "bg-terracotta text-primary-foreground" };
    return (
        <Card className="p-5 border-border flex items-center gap-4">
            <span className={`h-12 w-12 rounded-xl inline-flex items-center justify-center shrink-0 ${toneMap[tone]}`}><Icon className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
                <p className="font-serif text-2xl leading-tight">{formatMoney(total)}</p>
                <p className="text-xs text-muted-foreground">{count} records</p>
            </div>
        </Card>
    );
}

const ALL_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

function UnclaimedList({ items, state, onStateChange }) {
    if (!items || items.length === 0) {
        return (
            <section>
                <h2 className="font-serif text-2xl flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Unclaimed property</h2>
                <Card className="mt-4 p-8 text-center border-dashed border-border">
                    <p className="text-sm text-muted-foreground">No records yet — complete onboarding to run your scan.</p>
                </Card>
            </section>
        );
    }
    return (
        <section>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-serif text-2xl flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Unclaimed property</h2>
                <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <select value={state} onChange={(e) => onStateChange(e.target.value)} className="h-9 rounded-full border border-border bg-card text-sm px-3 cursor-pointer" data-testid="claims-state-picker">
                        {ALL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
                {items.map((c) => (
                    <Card key={c.id} className="p-5 border-border flex flex-col h-full" data-testid={`claims-unclaimed-${c.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-xs text-muted-foreground">{c.source} {c.state !== "MULTI" && <span className="ml-1 font-mono">{c.state}</span>}</p>
                                <p className="mt-2 font-serif text-lg leading-tight">{c.description}</p>
                            </div>
                            {c.confidence > 0 && <ConfidenceBadge value={c.confidence} />}
                        </div>
                        <div className="mt-5 flex items-end justify-between">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Estimated</p>
                                <p className="font-serif text-3xl">{c.amount > 0 ? formatMoney(c.amount) : "—"}</p>
                            </div>
                            <Badge variant="outline" className="bg-secondary/60 border-border text-xs font-normal">Simulated</Badge>
                        </div>
                        <div className="mt-5 pt-4 border-t border-border flex items-center justify-between gap-2">
                            <a href={c.portal_url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                                Open portal <ExternalLink className="h-3 w-3" />
                            </a>
                            <Button asChild size="sm" className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                                <Link to={`/app/detail/claim/${c.id}`} data-testid={`claims-claim-btn-${c.id}`}>Claim <ArrowUpRight className="h-3.5 w-3.5 ml-1" /></Link>
                            </Button>
                        </div>
                    </Card>
                ))}
            </div>
        </section>
    );
}

function CreditsList() {
    return (
        <section>
            <h2 className="font-serif text-2xl flex items-center gap-2"><Receipt className="h-5 w-5 text-accent" /> Missed tax credits</h2>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
                {TAX_CREDITS.map((c) => (
                    <Card key={c.id} className="p-6 border-border flex flex-col h-full">
                        <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-accent/10 border-accent/30 text-accent-foreground text-xs">{c.agency}</Badge>
                            <span className="text-xs text-muted-foreground font-mono">{c.years.map(y => `'${String(y).slice(2)}`).join(" · ")}</span>
                        </div>
                        <p className="mt-3 font-serif text-2xl leading-tight">{c.name}</p>
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.description}</p>
                        <div className="mt-auto pt-5 flex items-end justify-between">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Est. recovery</p>
                                <p className="font-serif text-3xl">{formatMoney(c.estimate)}</p>
                            </div>
                            <Button asChild size="sm" className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                                <Link to={`/app/detail/credit/${c.id}`}>File <ArrowUpRight className="h-3.5 w-3.5 ml-1" /></Link>
                            </Button>
                        </div>
                    </Card>
                ))}
            </div>
        </section>
    );
}

function SettlementsList() {
    return (
        <section>
            <h2 className="font-serif text-2xl flex items-center gap-2"><Landmark className="h-5 w-5 text-terracotta" /> Class action settlements</h2>
            <div className="mt-4 space-y-3">
                {SETTLEMENTS.map((s) => (
                    <Card key={s.id} className="p-5 border-border flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-serif text-lg leading-tight">{s.name}</p>
                                <Badge variant="outline" className={cn("text-xs", s.status === "Open" ? "bg-success/10 border-success/30 text-success" : "bg-secondary border-border text-muted-foreground")}>
                                    {s.status === "Open" ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />} {s.status}
                                </Badge>
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground">{s.description}</p>
                        </div>
                        <div className="flex items-center gap-5">
                            <div className="text-right"><p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Est.</p><p className="font-serif text-xl">{formatMoney(s.estimate)}</p></div>
                            <div className="text-right"><p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Deadline</p><p className="text-sm">{formatDeadline(s.deadline)}</p></div>
                            <Button asChild size="sm" variant="outline" className="rounded-full border-border"><Link to={`/app/detail/settlement/${s.id}`}>Details <ExternalLink className="h-3.5 w-3.5 ml-1" /></Link></Button>
                        </div>
                    </Card>
                ))}
            </div>
        </section>
    );
}

function ConfidenceBadge({ value }) {
    const tone = value >= 85 ? "bg-success/10 border-success/30 text-success" : value >= 70 ? "bg-warning/10 border-warning/30 text-warning" : "bg-muted border-border text-muted-foreground";
    return <div className={cn("rounded-full border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider", tone)}>{value}% match</div>;
}
